import * as THREE from 'three';
import { COLLECT_INDICATOR_MIN_COINS } from '../config/balancing';
import { catalogItem } from '../config/catalog';
import { revenuePerTick, storageCap } from '../sim/economy';
import type { EntityKind, PlacedEntity } from '../types';

/**
 * World coin cue visibility only. Income, storage caps, and collection amounts stay in balancing.
 * 20% of the current-level cap remains the latest a producing entity may wait.
 */
export const COLLECT_CUE_CAPACITY_RATIO = 0.2;

/** A producing entity may surface a cue after about this many seconds of current-level revenue. */
export const COLLECT_CUE_REVENUE_SECONDS = 10;

/**
 * Smallest integer stored-coin count at or above 20% of `cap`.
 * This is the upper bound, not the only visibility rule.
 * A missing, non-finite, or non-positive cap has no threshold.
 */
export function collectCueCoinThreshold(cap: number): number | null {
  if (!Number.isFinite(cap) || cap <= 0) {
    return null;
  }
  return Math.max(COLLECT_INDICATOR_MIN_COINS, Math.ceil(cap * COLLECT_CUE_CAPACITY_RATIO));
}

/**
 * Earlier of the 20% cap bound and about 10 seconds of current-level revenue.
 * Equivalent to `max(1, min(ceil(cap * 0.20), ceil(revenue * 10)))`.
 * Non-positive revenue is not a cue target.
 */
export function collectCueVisibilityThreshold(cap: number, revenue: number): number | null {
  const capThreshold = collectCueCoinThreshold(cap);
  if (capThreshold === null || !Number.isFinite(revenue) || revenue <= 0) {
    return null;
  }
  const revenueThreshold = Math.ceil(revenue * COLLECT_CUE_REVENUE_SECONDS);
  return Math.max(COLLECT_INDICATOR_MIN_COINS, Math.min(capThreshold, revenueThreshold));
}

/** Find or create the overlay under the existing HUD/root. No static index.html node. */
export function ensureCollectCueLayer(doc: Document = document): HTMLElement {
  const existing = doc.getElementById('collect-cues');
  if (existing) {
    return existing;
  }
  const layer = doc.createElement('div');
  layer.id = 'collect-cues';
  const host = doc.getElementById('hud') ?? doc.getElementById('app') ?? doc.body;
  host.appendChild(layer);
  return layer;
}

/** Producing entities show a one-click coin cue at the visibility threshold. */
export function collectCueEligible(entity: PlacedEntity): boolean {
  if (catalogItem(entity.kind).role === 'decoration') {
    return false;
  }
  const threshold = collectCueVisibilityThreshold(storageCap(entity), revenuePerTick(entity));
  if (threshold === null) {
    return false;
  }
  return Math.floor(entity.storedCoins) >= threshold;
}

/** World-Y lift above parkTop so the cue sits over the attraction/kiosk. */
export function collectCueHeight(kind: EntityKind): number {
  switch (kind) {
    case 'ferris_wheel':
      return 4.6;
    case 'roller_coaster':
      return 3.2;
    case 'pirate_ship':
      return 2.8;
    case 'spinning_ride':
      return 2.2;
    case 'starter_ride':
      return 2.4;
    case 'snack_kiosk':
    case 'drink_kiosk':
      return 1.6;
    default:
      return 1.2;
  }
}

export function projectWorldToOverlay(
  camera: THREE.Camera,
  canvas: HTMLElement,
  overlay: HTMLElement,
  world: THREE.Vector3,
): { x: number; y: number; visible: boolean } {
  const ndc = world.clone().project(camera);
  const canvasRect = canvas.getBoundingClientRect();
  const overlayRect = overlay.getBoundingClientRect();
  return {
    x: (ndc.x * 0.5 + 0.5) * canvasRect.width + (canvasRect.left - overlayRect.left),
    y: (-ndc.y * 0.5 + 0.5) * canvasRect.height + (canvasRect.top - overlayRect.top),
    visible: ndc.z >= -1 && ndc.z <= 1 && Math.abs(ndc.x) <= 1.2 && Math.abs(ndc.y) <= 1.2,
  };
}
